import { useEffect, useRef } from 'react';

const AnimatedBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Enhanced particles array with more properties
    const particles: Array<{
      x: number;
      y: number;
      z: number;
      vx: number;
      vy: number;
      vz: number;
      size: number;
      opacity: number;
      rotation: number;
      rotationSpeed: number;
      pulsePhase: number;
      pulseSpeed: number;
    }> = [];

    // Create enhanced particles
    for (let i = 0; i < 180; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        z: Math.random() * 1200,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        vz: Math.random() * 1.5 + 0.3,
        size: Math.random() * 2.5 + 0.8,
        opacity: Math.random() * 0.4 + 0.1,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.02,
        pulsePhase: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.03 + 0.01,
      });
    }

    let time = 0;

    const animate = () => {
      time += 0.01;
      
      // Enhanced background gradient with subtle animation
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, `rgba(2, 6, 23, ${0.95 + Math.sin(time) * 0.05})`);
      gradient.addColorStop(0.3, `rgba(13, 21, 46, ${0.95 + Math.sin(time * 1.1) * 0.05})`);
      gradient.addColorStop(0.7, `rgba(25, 25, 62, ${0.95 + Math.sin(time * 0.9) * 0.05})`);
      gradient.addColorStop(1, `rgba(31, 16, 52, ${0.95 + Math.sin(time * 1.2) * 0.05})`);
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle, index) => {
        // Update particle properties
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.z -= particle.vz;
        particle.rotation += particle.rotationSpeed;
        particle.pulsePhase += particle.pulseSpeed;

        // Reset particle if it goes too far
        if (particle.z <= 0) {
          particle.x = Math.random() * canvas.width;
          particle.y = Math.random() * canvas.height;
          particle.z = 1200;
        }

        // Wrap around edges with smooth transition
        if (particle.x < -50) particle.x = canvas.width + 50;
        if (particle.x > canvas.width + 50) particle.x = -50;
        if (particle.y < -50) particle.y = canvas.height + 50;
        if (particle.y > canvas.height + 50) particle.y = -50;

        // Enhanced 3D projection
        const scale = 300 / (300 + particle.z);
        const x2d = particle.x * scale + canvas.width / 2 * (1 - scale);
        const y2d = particle.y * scale + canvas.height / 2 * (1 - scale);
        const size2d = particle.size * scale;
        
        // Dynamic pulsing effect
        const pulse = Math.sin(particle.pulsePhase) * 0.3 + 1;
        const finalSize = size2d * pulse;

        // Enhanced color system with more depth
        const depth = 1 - particle.z / 1200;
        const hue = 210 + depth * 60 + Math.sin(time + particle.pulsePhase) * 15;
        const saturation = 60 + depth * 30;
        const lightness = 40 + depth * 40;
        const alpha = particle.opacity * scale * (0.6 + Math.sin(particle.pulsePhase) * 0.4);

        // Enhanced glow effect with multiple layers
        ctx.save();
        ctx.translate(x2d, y2d);
        ctx.rotate(particle.rotation);

        // Outer glow
        const outerGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, finalSize * 4);
        outerGlow.addColorStop(0, `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha * 0.3})`);
        outerGlow.addColorStop(0.5, `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha * 0.1})`);
        outerGlow.addColorStop(1, `hsla(${hue}, ${saturation}%, ${lightness}%, 0)`);

        ctx.fillStyle = outerGlow;
        ctx.beginPath();
        ctx.arc(0, 0, finalSize * 4, 0, Math.PI * 2);
        ctx.fill();

        // Inner glow
        const innerGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, finalSize * 2);
        innerGlow.addColorStop(0, `hsla(${hue}, ${saturation + 20}%, ${lightness + 20}%, ${alpha * 0.6})`);
        innerGlow.addColorStop(0.7, `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha * 0.3})`);
        innerGlow.addColorStop(1, `hsla(${hue}, ${saturation}%, ${lightness}%, 0)`);

        ctx.fillStyle = innerGlow;
        ctx.beginPath();
        ctx.arc(0, 0, finalSize * 2, 0, Math.PI * 2);
        ctx.fill();

        // Core particle with subtle shape variation
        const coreGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, finalSize);
        coreGradient.addColorStop(0, `hsla(${hue + 20}, ${saturation + 30}%, ${Math.min(lightness + 40, 90)}%, ${alpha * 0.9})`);
        coreGradient.addColorStop(0.6, `hsla(${hue}, ${saturation + 20}%, ${lightness + 20}%, ${alpha * 0.7})`);
        coreGradient.addColorStop(1, `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha * 0.4})`);

        ctx.fillStyle = coreGradient;
        ctx.beginPath();
        
        // Subtle hexagonal shape for more premium feel
        const sides = 6;
        const angleStep = (Math.PI * 2) / sides;
        ctx.moveTo(finalSize, 0);
        for (let i = 1; i <= sides; i++) {
          const angle = i * angleStep;
          const radius = finalSize * (0.9 + Math.sin(particle.pulsePhase + i) * 0.1);
          ctx.lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
        }
        ctx.closePath();
        ctx.fill();

        ctx.restore();

        // Enhanced connection lines with better aesthetics
        particles.slice(index + 1).forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const dz = particle.z - otherParticle.z;
          const distance3D = Math.sqrt(dx * dx + dy * dy + dz * dz);

          if (distance3D < 150) {
            const otherScale = 300 / (300 + otherParticle.z);
            const otherX2d = otherParticle.x * otherScale + canvas.width / 2 * (1 - otherScale);
            const otherY2d = otherParticle.y * otherScale + canvas.height / 2 * (1 - otherScale);

            const connectionAlpha = 0.15 * scale * otherScale * (1 - distance3D / 150);
            const connectionHue = (hue + depth * 30) % 360;
            
            // Gradient line for more premium feel
            const lineGradient = ctx.createLinearGradient(x2d, y2d, otherX2d, otherY2d);
            lineGradient.addColorStop(0, `hsla(${connectionHue}, 70%, 60%, ${connectionAlpha})`);
            lineGradient.addColorStop(0.5, `hsla(${connectionHue + 20}, 80%, 70%, ${connectionAlpha * 1.5})`);
            lineGradient.addColorStop(1, `hsla(${connectionHue}, 70%, 60%, ${connectionAlpha})`);

            ctx.strokeStyle = lineGradient;
            ctx.lineWidth = 0.8 + Math.sin(time + particle.pulsePhase) * 0.3;
            ctx.beginPath();
            ctx.moveTo(x2d, y2d);
            ctx.lineTo(otherX2d, otherY2d);
            ctx.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full -z-10 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
      style={{
        background: 'linear-gradient(135deg, #020617 0%, #0d152e 25%, #19193e 75%, #1f1034 100%)'
      }}
    />
  );
};

export default AnimatedBackground;